import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type {
  Account,
  BackgroundAccountReason,
  BackgroundPushReason,
} from "@/api";
import { useApi } from "@/api/provider";
import { STALE_TIMES } from "@/api/queryClient";
import { useAccountContext } from "@/app/accounts";
import {
  needsPassword,
  ReenterPasswordLink,
} from "@/components/AccountSwitcher";
import { BACKGROUND_PUSH_QUERY } from "@/realtime/reminderNotice";
import { REAUTH_ACCOUNT_FOCUS } from "@/routes/settings/sections";
import { NoticeRow } from "./NoticeRow";
import { reauthBannerAccount } from "./ReauthBanner";
import { BACKGROUND_ACCOUNTS_QUERY } from "./reenter";

/**
 * The same key Settings → Notifications and Settings → Accounts use for
 * GET /push/background/accounts, so a change made there (turning a mailbox on
 * again, dismissing its notice) clears this banner without a reload.
 */
export { BACKGROUND_ACCOUNTS_QUERY };

/**
 * Settings → Notifications' query parameter naming the mailbox whose stored
 * password to enter again (backend CredentialNoticeParam). The push notice's
 * click carries it too.
 */
export const BACKGROUND_PARAM = "background";

/** Where "turn background notifications on again" lives for one mailbox. */
export function backgroundSettingsPath(accountId: string): string {
  return `/settings/notifications?${new URLSearchParams({ [BACKGROUND_PARAM]: accountId }).toString()}`;
}

interface Broken {
  account: Account;
  reason: BackgroundAccountReason | BackgroundPushReason;
  reasonAt: string;
}

/**
 * "<mailbox> needs its password again", across the top of the app (#210).
 *
 * A stored password the server withdrew (`needs_reconsent`: the mail host
 * refused it, the host moved, or a password was changed) silently stops new
 * mail notices, reminders, Send later and Snooze for that mailbox. Settings
 * said so, but only to someone who went to look; the server also pushes a
 * notice once, but only to a device that has notifications on. This says it
 * wherever the owner is in the app, for as long as it is true.
 *
 * The action goes where the fix is. A connected mailbox whose own saved
 * password was refused (`reauth-required`) needs that password first: turning
 * background notifications on again would only re-seal the same refused one.
 * So its row offers Re-enter password, which opens the shell's password modal
 * (#192); and while it is the active mailbox the can't-sign-in row beside this
 * one (ReauthBanner) already says so, and this one stays out of the way: one
 * row per mailbox. Saving the password withdraws the background copy, so
 * "Turn back on" follows once it is fixed. A connected mailbox in any other
 * unhealthy state still goes to Settings → Accounts first; the rest go
 * straight to their switch in Settings → Notifications, with the form open.
 *
 * Dismissing hides it for this page load only: it is back on the next load
 * while the mailbox still needs it, and a new breakage (a later `reasonAt`)
 * shows again at once. The permanent dismissal is the notice's own Dismiss in
 * Settings, which deletes the withdrawn row on the server.
 *
 * A server without background notifications answers 501 here, and any failure
 * renders nothing: this is a reminder, not the settings page.
 */
export function CredentialBanner() {
  const api = useApi();
  const { accounts, activeAccount } = useAccountContext();
  const askedFor = reauthBannerAccount(activeAccount)?.id;
  const primary = accounts.find((account) => account.isPrimary);
  const hasConnected = accounts.some((account) => !account.isPrimary);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const primaryState = useQuery({
    queryKey: BACKGROUND_PUSH_QUERY,
    queryFn: ({ signal }) => api.push.getBackgroundPush(signal),
    enabled: primary !== undefined,
    staleTime: STALE_TIMES.metadata,
    retry: false,
  });
  const connectedState = useQuery({
    queryKey: BACKGROUND_ACCOUNTS_QUERY,
    queryFn: ({ signal }) => api.push.listBackgroundAccounts(signal),
    enabled: hasConnected,
    staleTime: STALE_TIMES.metadata,
    retry: false,
  });

  const broken: Broken[] = [];
  if (primary && primaryState.data?.status === "needs_reconsent") {
    broken.push({
      account: primary,
      reason: primaryState.data.reason,
      reasonAt: primaryState.data.reasonAt ?? "",
    });
  }
  for (const entry of connectedState.data ?? []) {
    if (entry.status !== "needs_reconsent") continue;
    const account = accounts.find(
      (candidate) => candidate.id === entry.accountId,
    );
    // The can't-sign-in row beside this one is already asking for it.
    if (account && account.id !== askedFor)
      broken.push({
        account,
        reason: entry.reason,
        reasonAt: entry.reasonAt ?? "",
      });
  }
  const showing = broken.filter((item) => !dismissed.has(dismissKey(item)));
  if (showing.length === 0) return null;

  return (
    <div className="credential-banners">
      {showing.map((item) => (
        <CredentialBannerRow
          key={item.account.id}
          item={item}
          onDismiss={() =>
            setDismissed((prev) => new Set(prev).add(dismissKey(item)))
          }
        />
      ))}
    </div>
  );
}

function dismissKey(item: Broken): string {
  return `${item.account.id}\u0000${item.reasonAt}`;
}

function CredentialBannerRow({
  item,
  onDismiss,
}: {
  item: Broken;
  onDismiss: () => void;
}) {
  const { account } = item;
  const label = account.displayName.trim() || account.email;
  // The mailbox itself is flagged: its own password comes first.
  const reenterFirst = !account.isPrimary && account.status !== "connected";
  return (
    <NoticeRow
      label={`${label} needs its password again`}
      dismissLabel={`Dismiss the notice for ${label}`}
      onDismiss={onDismiss}
      action={
        needsPassword(account) ? (
          // The shell's password modal, or Settings → Accounts outside it.
          <ReenterPasswordLink account={account} className="btn btn--ghost" />
        ) : reenterFirst ? (
          <Link
            className="btn btn--ghost"
            to="/settings/accounts"
            state={{ focus: REAUTH_ACCOUNT_FOCUS, accountId: account.id }}
          >
            Re-enter password
          </Link>
        ) : (
          <Link
            className="btn btn--ghost"
            to={backgroundSettingsPath(account.id)}
          >
            Turn back on
          </Link>
        )
      }
    >
      <strong>{label}</strong> needs its password again. New-mail
      notifications, reminders, Send later and Snooze have stopped for it.
    </NoticeRow>
  );
}
